import React from "react";
import { JOB_TYPES } from "@/lib/constants";
import { Tooltip, TooltipContent, TooltipTrigger } from "ui/components/tooltip";
import ReportJobFilters from "./filters";
import { List, Icon } from "components";
import { formatDateForDistance, ROUTE } from "@/lib/utils";
import { DateCell } from "@/components/date/cell";
import { StatusCell } from "@/components/status/cell";
import { UtilizationCell } from "@/components/cells/jobs/utilization";
import { EmployeeCell } from "components/cell/employee/cell";
import JobNameCell from "@/components/cells/jobs/name-cell";
import { getEmployeeFields } from "components/user/config";

export default function ExtentionsJobsReports() {
  const fields = [
    "id",
    "name",
    "type",
    "status",
    "flag",
    "project.project_id",
    "project.name",
    "start_date",
    "end_date",
    "extensions.start_date",
    "extensions.end_date",
    "extensions.date_created",
    "extensions.reason",
    "extensions.reason.label",
    getEmployeeFields("coordinator"),
    getEmployeeFields("project.coordinator"),
    getEmployeeFields("project.account_manager"),
    getEmployeeFields("project.sales_coordinator"),
    "allocated_hours.hours",
    "bucket_job_config.hours",
    "timesheet.logged_hours",
    "timesheet.employee.consumption_factor",
    "timesheet.consumption_factor",
  ];

  const columns = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      sortable: true,
      cell: JobNameCell,
    },
    {
      id: "type",
      header: "Type",
      accessorKey: "type",
      cell: ({ getValue }) => {
        const type = JOB_TYPES[getValue()];
        if (!type) return null;
        return (
          <Tooltip>
            <TooltipTrigger>
              <Icon icon={type.icon} className="text-muted-foreground size-4" />
            </TooltipTrigger>
            <TooltipContent>{type.label}</TooltipContent>
          </Tooltip>
        );
      },
      sortable: false,
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      cell: StatusCell,
    },
    {
      id: "project.name",
      header: "Project",
      accessorKey: "project.name",
    },
    {
      id: "start_date",
      header: "Start Date",
      accessorKey: "start_date",
      sortable: true,
      cell: DateCell,
    },
    {
      id: "end_date",
      header: "End Date",
      accessorKey: "end_date",
      sortable: true,
      cell: DateCell,
    },
    {
      id: "extensions",
      header: "Extended",
      accessorKey: "extensions",
      cell: (info) => {
        const extensions = info.row.original.extensions;
        if (!Array.isArray(extensions) || extensions.length === 0) return null;

        const end_date = info.row.original.end_date;
        const startDate = extensions[0].start_date;
        const endDate = end_date;

        const isNegative = endDate < startDate;
        const days = isNegative
          ? formatDateForDistance(startDate, endDate)
          : formatDateForDistance(endDate, startDate);

        const displayDays = isNegative ? -Math.abs(days) : days;
        return `${displayDays} day${Math.abs(displayDays) === 1 ? "" : "s"}`;
      },
    },
    {
      id: "coordinator",
      header: "JC",
      accessorKey: "coordinator",
      cell: EmployeeCell,
    },
    {
      id: "project.coordinator",
      header: "PC",
      accessorKey: "project.coordinator",
      cell: EmployeeCell,
    },
    {
      id: "project.account_manager",
      header: "AM",
      accessorKey: "project.account_manager",
      cell: EmployeeCell,
    },
    {
      id: "project.sales_coordinator",
      header: "SC",
      accessorKey: "project.sales_coordinator",
      cell: EmployeeCell,
    },
    {
      id: "extensions",
      header: "Extension reason",
      accessorKey: "extensions",
      cell: ({ row }) => {
        const extensions = row.original.extensions || [];
        const lastExtension = extensions.length > 0 ? extensions[extensions.length - 1] : null;
        return lastExtension?.reason?.label;
      },
    },
    {
      id: "hours_totals",
      header: () => {
        return (
          <div className="flex flex-col">
            <span>Utilization</span>
            <span className="text-xs text-muted-foreground">
              Spent / Allocated
            </span>
          </div>
        );
      },
      accessorKey: "timesheet",
      cell: UtilizationCell,
    },
  ];

  const defaultFilters = {
    project: { _nnull: true },
    extensions: { _nnull: true },
    status: { _nin: ["archived", "closed", "cancelled"] },
  };

  const deep = {
    extensions: {
      _sort: ["date_created"],
    },
    timesheet: {
      _limit: -1,
    },
    allocated_hours: {
      _limit: -1,
    },
  };

  const defaultHref = (item) => ROUTE.JOB_EXTENSIONS(item.id);

  return (
    <List
      title="Jobs Extension Report"
      storageKey="jobs-extension-report"
      endPoint="jobs"
      filters={defaultFilters}
      columns={columns}
      meta={{ fields, deep }}
      sortBy="end_date"
      href={defaultHref}
      renderFilters={ReportJobFilters}
      // hidePagination={false}
      showSearch={false}
    />
  );
}
